import { Link, useSearchParams } from 'react-router-dom';
import { CollapsibleSection } from '../components/goals/CollapsibleSection';
import { EmptyState } from '../components/EmptyState';
import { ServerTabStrip, type ServerTabItem } from '../components/links/ServerTabStrip';
import {
  accountLegionTotals,
  GRADE_ORDER,
  legionEffectText,
  rankOfGrade,
  type LegionGrade,
} from '../lib/legion';
import { useLegionProgress, useUpsertLegionProgress } from '../lib/legionProgress';
import { groupClassesByBranch, useClasses, useWorlds, type ClassDto } from '../lib/reference';
import { useAccounts } from '../lib/roster';

const GRADE_LABEL: Record<LegionGrade, string> = { NONE: 'None', B: 'B', A: 'A', S: 'S', SS: 'SS', SSS: 'SSS' };

const CHARACTERS_BADGE_CLASSES =
  'inline-flex items-center rounded-full bg-accent-dim px-2.5 py-0.5 text-xs font-semibold text-accent-strong transition-colors hover:bg-accent hover:text-accent-contrast';

const filledBadgeClasses =
  'flex min-h-tap min-w-tap items-center justify-center rounded-full bg-accent-dim px-2.5 text-[0.7rem] font-semibold uppercase tracking-wide text-accent-strong';
const outlinedBadgeClasses =
  'flex min-h-tap min-w-tap items-center justify-center rounded-full border border-border-strong bg-bg-elevated px-2.5 text-[0.7rem] font-semibold uppercase tracking-wide text-text-faint';

function LegionClassRow({
  klass,
  accountId,
  derivedGrade,
  effectiveGrade,
  derivedFromCharacterName,
  derivedFromLevel,
  pending,
}: {
  klass: ClassDto;
  accountId: number;
  derivedGrade: string;
  effectiveGrade: string;
  derivedFromCharacterName: string | null;
  derivedFromLevel: number | null;
  pending: boolean;
}) {
  const upsert = useUpsertLegionProgress(accountId);
  const floorRank = rankOfGrade(derivedGrade);

  return (
    <li className="card-hover flex flex-wrap items-center gap-3 rounded-lg border border-border bg-bg-elevated-hover px-3 py-2">
      <span
        aria-label={`${klass.name} legion grade: ${effectiveGrade === 'NONE' ? 'not attained' : effectiveGrade}`}
        className={effectiveGrade === 'NONE' ? outlinedBadgeClasses : filledBadgeClasses}
      >
        {effectiveGrade === 'NONE' ? '—' : effectiveGrade}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-text">{klass.name}</p>
        <p className="truncate text-xs text-text-faint">{legionEffectText(klass)}</p>
        {derivedFromCharacterName && (
          <p className="text-xs italic text-text-muted">
            from roster: {derivedFromCharacterName} Lv {derivedFromLevel}
          </p>
        )}
      </div>
      <select
        aria-label={`Legion grade for ${klass.name}`}
        value={effectiveGrade}
        disabled={pending}
        onChange={(e) => upsert.mutate({ classSlug: klass.slug, grade: e.target.value })}
        className="min-h-tap rounded-lg border border-border-strong bg-bg-elevated px-3 text-sm text-text focus:border-accent focus:outline-none disabled:opacity-50"
      >
        {GRADE_ORDER.map((grade) => (
          <option key={grade} value={grade} disabled={rankOfGrade(grade) < floorRank}>
            {GRADE_LABEL[grade]}
            {rankOfGrade(grade) < floorRank ? ' (below roster floor)' : ''}
          </option>
        ))}
      </select>
    </li>
  );
}

/**
 * `/legion` — REV8: the account-wide Legion member-grade tracker, mirroring `/links`' shape
 * (server tabs, attained/unattained split, `CollapsibleSection` branch groups). No grid/coin/
 * artifact modeling here — doc 01 §5 records the Tetris Synergy Grid and Legion Raid are being
 * replaced (KMS Overdrive -> GMS ~fall/winter 2026); grades-per-member is the durable core this
 * page tracks.
 */
export function Legion() {
  const accountsQuery = useAccounts();
  const classesQuery = useClasses();
  const worldsQuery = useWorlds();
  const [searchParams, setSearchParams] = useSearchParams();

  const accounts = accountsQuery.data ?? [];
  const worlds = worldsQuery.data ?? [];

  const accountParam = searchParams.get('account');
  const requestedAccountId = accountParam !== null ? Number(accountParam) : null;
  const validAccountIds = new Set(accounts.map((a) => a.id));
  const effectiveAccountId =
    requestedAccountId !== null && validAccountIds.has(requestedAccountId)
      ? requestedAccountId
      : (accounts[0]?.id ?? null);

  const selectTab = (id: number) => {
    setSearchParams((params) => {
      const next = new URLSearchParams(params);
      next.set('account', String(id));
      return next;
    });
  };

  const legionProgressQuery = useLegionProgress(effectiveAccountId);
  const upsert = useUpsertLegionProgress(effectiveAccountId ?? -1);

  if (accountsQuery.isPending || classesQuery.isPending) {
    return <p className="text-sm text-text-muted">Loading legion…</p>;
  }
  if (accountsQuery.isError || classesQuery.isError) {
    return (
      <p role="alert" className="text-sm text-danger">
        Could not load legion. Please retry in a moment.
      </p>
    );
  }
  if (accounts.length === 0) {
    return (
      <EmptyState
        title="No accounts yet"
        note="Add your main under Characters and let's track your legion."
        actionTo="/characters"
        actionLabel="Go to Characters"
      />
    );
  }

  const classes = classesQuery.data ?? [];
  const progress = legionProgressQuery.data ?? [];
  const progressBySlug = new Map(progress.map((p) => [p.classSlug, p]));
  const selectedAccount = accounts.find((a) => a.id === effectiveAccountId) ?? null;
  const totals = accountLegionTotals(classes, progress, selectedAccount?.characters ?? []);
  const pendingSlug = upsert.isPending ? upsert.variables?.classSlug : null;

  const attainedClasses = classes.filter(
    (k) => (progressBySlug.get(k.slug)?.effectiveGrade ?? 'NONE') !== 'NONE',
  );
  const unattainedClasses = classes.filter(
    (k) => (progressBySlug.get(k.slug)?.effectiveGrade ?? 'NONE') === 'NONE',
  );
  const attainedBranches = groupClassesByBranch(attainedClasses);

  // Tab list = every account, labeled by its world — identical convention to /links.
  const tabs: ServerTabItem[] = accounts.map((a) => ({
    id: a.id,
    label: worlds.find((w) => w.slug === a.worldSlug)?.name ?? a.worldSlug,
    microtext: a.label,
  }));

  const renderRow = (klass: ClassDto) => {
    const p = progressBySlug.get(klass.slug);
    return (
      <LegionClassRow
        key={klass.slug}
        klass={klass}
        accountId={effectiveAccountId ?? -1}
        derivedGrade={p?.derivedGrade ?? 'NONE'}
        effectiveGrade={p?.effectiveGrade ?? 'NONE'}
        derivedFromCharacterName={p?.derivedFromCharacterName ?? null}
        derivedFromLevel={p?.derivedFromLevel ?? null}
        pending={pendingSlug === klass.slug}
      />
    );
  };

  return (
    <div className="stagger-children space-y-5">
      <div>
        <h1 className="font-display text-xl font-semibold text-text">Legion</h1>
        <p className="mt-1 text-sm text-text-muted">Maple Union members and grades, account-wide.</p>
      </div>

      <ServerTabStrip tabs={tabs} selectedId={effectiveAccountId ?? -1} onSelect={selectTab} idPrefix="legion" />

      {/* Same integrated summary strip recipe as /links (docs/DESIGN.md: "no uniform card-grid
          dashboards, no oversized vanity numbers"). */}
      <div className="grid grid-cols-3 divide-x divide-border rounded-lg border border-border bg-bg-elevated">
        <div className="p-3">
          <p className="font-display text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-text-faint">
            Total legion level (approx.)
          </p>
          <p className="text-lg font-semibold tabular-nums text-crystal">{totals.totalLegionLevel}</p>
        </div>
        <div className="p-3">
          <p className="font-display text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-text-faint">
            Members
          </p>
          <p className="text-lg font-semibold tabular-nums text-text">{totals.memberCount}</p>
        </div>
        <div className="p-3">
          <p className="font-display text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-text-faint">
            SSS members
          </p>
          <p className="text-lg font-semibold tabular-nums text-text">{totals.sssCount}</p>
        </div>
      </div>

      {legionProgressQuery.isPending ? (
        <p className="text-sm text-text-muted">Loading this account's legion progress…</p>
      ) : legionProgressQuery.isError ? (
        <p role="alert" className="text-sm text-danger">
          Could not load legion progress.
        </p>
      ) : (
        <div id="legion-tabpanel" role="tabpanel" aria-label="Legion for the selected server">
          <div className="space-y-5">
            {[...attainedBranches.entries()].map(([branch, branchClasses]) => (
              <CollapsibleSection key={branch} title={branch} subtitle={`${branchClasses.length} classes`}>
                <ul className="space-y-2">{branchClasses.map(renderRow)}</ul>
              </CollapsibleSection>
            ))}

            {unattainedClasses.length > 0 && (
              <CollapsibleSection
                title="Not yet attained"
                subtitle={`${unattainedClasses.length} classes`}
                defaultOpen={false}
              >
                <p className="text-sm text-text-muted">
                  Have one of these members? Add that character under{' '}
                  <Link to="/characters" className={CHARACTERS_BADGE_CLASSES}>
                    Characters
                  </Link>{' '}
                  and it'll show up here automatically.
                </p>
                <ul className="space-y-2">{unattainedClasses.map(renderRow)}</ul>
              </CollapsibleSection>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
